import { useEffect, useRef, RefObject } from 'react';

type UseBannerScaleToFitArgs = {
  isEnabled: boolean;
  containerRef: RefObject<HTMLElement | null>;
  baseWidth: number;
  baseHeight: number;
};

/**
 * Scales a banner to fit the container width while preserving aspect ratio.
 * Uses the rendered slot size, falling back to baseWidth/baseHeight before the ad loads.
 * Sets CSS variables --hz-banner-scale and --hz-banner-height on the container.
 * Debounces via requestAnimationFrame to avoid ResizeObserver loops.
 */
export function useBannerScaleToFit({ isEnabled, containerRef, baseWidth, baseHeight }: UseBannerScaleToFitArgs) {
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isEnabled || typeof window === 'undefined') return;
    const containerEl = containerRef.current;
    if (!containerEl) return;

    // the slot; a native sized to the container must not be scaled, and can be taller than baseHeight
    const contentEl = containerEl.firstElementChild as HTMLElement | null;

    const scale = () => {
      const availableWidth = containerEl.clientWidth;
      if (!availableWidth) return;

      const nextScale = Math.min(1, availableWidth / (contentEl?.scrollWidth || baseWidth));
      containerEl.style.setProperty('--hz-banner-scale', String(nextScale));
      const roundedHeight = Math.round((contentEl?.offsetHeight || baseHeight) * nextScale);
      containerEl.style.setProperty('--hz-banner-height', `${roundedHeight}px`);
    };

    const scheduleScale = () => {
      if (rafIdRef.current != null) return;
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        scale();
      });
    };

    const ro = new ResizeObserver(() => scheduleScale());
    ro.observe(containerEl);
    if (contentEl) ro.observe(contentEl);

    // Initial scale
    scheduleScale();

    return () => {
      ro.disconnect();
      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [isEnabled, containerRef, baseWidth, baseHeight]);
}

export default useBannerScaleToFit;
